import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useExtractBill, useSendFunnelEnergyPlanPreview } from "@/api/hooks";
import { useAuth } from "@/_core/hooks/useAuth";
import { useQuoteFlow } from "@/contexts/QuoteFlowContext";
import { useAuthContactFields } from "@/hooks/useAuthContactFields";
import type { BillData } from "@/api/types";
import { prepareBillFile } from "@/utils/billFileValidation";
import { gbp } from "@/utils/phoenixFinanceExample";
import { nameAfterBillUpload, processBillUpload } from "@/utils/quoteBill";
import EmailPlanConsent from "./EmailPlanConsent";
import ToggleRow from "./ToggleRow";
import { ApiError } from "@/api/client";
import { toast } from "sonner";

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

type Step = "upload" | "reading" | "confirm" | "details" | "result";

function formatKwh(value: number | null): string {
  if (value == null) return "Not found";
  return `${Math.round(value).toLocaleString("en-GB")} kWh`;
}

function formatStanding(value: number | null): string {
  if (value == null) return "Not found";
  return `${value.toLocaleString("en-GB", { maximumFractionDigits: 2 })}p/day`;
}

export default function BillUploadFlow() {
  const [step, setStep] = useState<Step>("upload");
  const [fileName, setFileName] = useState<string | null>(null);
  const [usage, setUsage] = useState<number | null>(null);
  const [standingCharge, setStandingCharge] = useState<number | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [ev, setEv] = useState(false);
  const [heatPump, setHeatPump] = useState(false);
  const { user, loading: authLoading, isAuthenticated } = useAuth();
  const { name, setName, email, setEmail, phone, setPhone } = useAuthContactFields();
  const [billCustomerName, setBillCustomerName] = useState<string | null>(null);
  const [emailConsent, setEmailConsent] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestIdRef = useRef(0);
  const billFileRef = useRef<File | null>(null);
  const billDataRef = useRef<BillData | null>(null);
  const extractBill = useExtractBill();
  const sendPreviewEmail = useSendFunnelEnergyPlanPreview();
  const { setPendingBillFile, setPendingBillData, setPendingPlanContact } = useQuoteFlow();
  const [, navigate] = useLocation();

  const currentMonthlyBill = useMemo(() => {
    if (usage == null && standingCharge == null) return null;
    const usageCost = usage != null ? usage * 0.27 : 0;
    const standingCost = standingCharge != null ? (standingCharge / 100) * 365 : 0;
    return Math.round((usageCost + standingCost) / 12);
  }, [usage, standingCharge]);

  const result = useMemo(() => {
    const base = 1600 + (usage != null ? (usage / 40) * 6 : 0) + (ev ? 220 : 0) + (heatPump ? 260 : 0);
    const panels = 8 + (heatPump ? 4 : 0) + (ev ? 2 : 0);
    const kwp = Math.round(panels * 0.48 * 100) / 100;
    const battery = ev || heatPump ? 11.5 : 5;
    const annualBenefit = Math.round(base / 10) * 10;
    const monthlySaving = Math.round(annualBenefit / 12);
    const cappedMonthlySaving =
      currentMonthlyBill == null
        ? monthlySaving
        : Math.min(monthlySaving, Math.round(currentMonthlyBill * 0.85));
    const newMonthlyBill =
      currentMonthlyBill == null ? null : Math.max(5, currentMonthlyBill - cappedMonthlySaving);
    return {
      annualBenefit,
      monthlySaving: cappedMonthlySaving,
      newMonthlyBill,
      panels,
      kwp,
      battery,
    };
  }, [usage, ev, heatPump, currentMonthlyBill]);

  useEffect(() => {
    if (authLoading) return;
    setName((current) =>
      nameAfterBillUpload({
        currentName: current,
        billName: billCustomerName,
        signedIn: isAuthenticated,
      }),
    );
  }, [authLoading, isAuthenticated, billCustomerName, setName]);

  async function onFile(f: File | null) {
    if (!f) return;
    const { file, error } = prepareBillFile(f);
    if (error) {
      setFileError(error);
      setExtractError(null);
      return;
    }

    setFileError(null);
    setExtractError(null);
    setFileName(file.name);
    setStep("reading");
    const requestId = ++requestIdRef.current;
    const found: { data: BillData | null } = { data: null };

    try {
      await processBillUpload<{ billData?: BillData }>(file, extractBill.mutateAsync, (patch) => {
        if (patch.billData) found.data = patch.billData;
      });
    } catch {
      if (requestId !== requestIdRef.current) return;
      setExtractError("We couldn't read that bill. Try another file.");
      setStep("upload");
      return;
    }

    if (requestId !== requestIdRef.current) return;
    if (!found.data) {
      setExtractError("We couldn't read that bill. Try another file.");
      setStep("upload");
      return;
    }

    const annualKwh = found.data.annual_kwh;
    const standing = found.data.standing_charge_daily_pence;
    setUsage(annualKwh != null && annualKwh > 0 ? annualKwh : null);
    setStandingCharge(standing != null && standing > 0 ? standing : null);
    billFileRef.current = file;
    billDataRef.current = found.data;
    setBillCustomerName(found.data.customer_name?.trim() || null);
    setStep("confirm");
  }

  function startAgain() {
    requestIdRef.current += 1;
    setStep("upload");
    setFileName(null);
    setUsage(null);
    setStandingCharge(null);
    setFileError(null);
    setExtractError(null);
    setEv(false);
    setHeatPump(false);
    setBillCustomerName(null);
    if (user) {
      setName(user.name?.trim() ?? "");
      setEmail(user.email?.trim() ?? "");
      setPhone(user.phone?.trim() ?? "");
    } else {
      setName("");
      setEmail("");
      setPhone("");
    }
    setEmailConsent(false);
    setPreviewError(null);
    billFileRef.current = null;
    billDataRef.current = null;
  }

  return (
    <div className="flex w-full max-w-2xl flex-col gap-5 rounded-3xl border-2 border-ink bg-white p-6 shadow-hard sm:p-8">
      {step === "upload" && (
        <div className="flex flex-col gap-4">
          <div className="text-xs font-bold uppercase tracking-widest text-indigo">Upload a bill, get your exact design</div>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={(e) => {
              if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
              setDragging(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              void onFile(e.dataTransfer.files?.[0] ?? null);
            }}
            className={`flex min-h-[180px] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed bg-cream text-center ${
              dragging ? "border-indigo" : "border-border"
            }`}
          >
            <span className="font-display text-lg font-bold">Tap to upload, or drag a file here</span>
            <span className="text-sm text-mutedLight">A photo or PDF of a recent bill</span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
            className="hidden"
            onChange={(e) => {
              void onFile(e.target.files?.[0] ?? null);
              e.target.value = "";
            }}
          />
          {fileError && <p className="m-0 text-sm font-semibold text-red-600">{fileError}</p>}
          {extractError && <p className="m-0 text-sm font-semibold text-red-600">{extractError}</p>}
          <p className="m-0 text-xs leading-relaxed text-mutedLight">
            Your file is sent so we can read the usage and standing charge. The savings estimate later in this
            flow is illustrative, based on those figures.
          </p>
        </div>
      )}

      {step === "reading" && (
        <div className="flex flex-col items-center gap-4 py-10 text-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-border border-t-indigo" />
          <div className="font-display text-xl font-bold">Reading your bill…</div>
          <div className="text-sm text-mutedLight">{fileName}</div>
        </div>
      )}

      {step === "confirm" && (
        <div className="flex flex-col gap-4">
          <div className="text-xs font-bold uppercase tracking-widest text-indigo">Here&apos;s what we read</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-border bg-cream p-4">
              <div className="text-sm text-muted">Annual usage</div>
              <div className="font-display text-2xl font-bold">{formatKwh(usage)}</div>
            </div>
            <div className="rounded-2xl border border-border bg-cream p-4">
              <div className="text-sm text-muted">Standing charge</div>
              <div className="font-display text-2xl font-bold">{formatStanding(standingCharge)}</div>
            </div>
          </div>
          <p className="m-0 text-xs text-mutedLight">
            Read from your bill. A missing figure is shown as not found, rather than guessed.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setStep("details")}
              className="min-h-[56px] rounded-2xl bg-lime px-6 font-display text-lg font-bold tracking-tight text-ink sm:flex-1"
            >
              Looks right, continue →
            </button>
            <button
              type="button"
              onClick={startAgain}
              className="min-h-[56px] rounded-2xl border-2 border-border px-6 font-semibold text-ink"
            >
              Start again
            </button>
          </div>
        </div>
      )}

      {step === "details" && (
        <div className="flex flex-col gap-4">
          <div className="text-xs font-bold uppercase tracking-widest text-indigo">A few quick questions</div>
          <ToggleRow
            label="Electric car in the picture?"
            sub="Charged at home, about 6,000 miles a year"
            checked={ev}
            onChange={setEv}
          />
          <ToggleRow
            label="Air source heat pump?"
            sub="Or planning to install one — it changes what system makes sense"
            checked={heatPump}
            onChange={setHeatPump}
          />
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => setStep("result")}
              className="min-h-[56px] rounded-2xl bg-lime px-6 font-display text-lg font-bold tracking-tight text-ink sm:flex-1"
            >
              Build my design →
            </button>
            <button
              type="button"
              onClick={startAgain}
              className="min-h-[56px] rounded-2xl border-2 border-border px-6 font-semibold text-ink"
            >
              Start again
            </button>
          </div>
        </div>
      )}

      {step === "result" && (
        <div className="flex flex-col gap-4">
          <div className="text-xs font-bold uppercase tracking-widest text-indigo">Solar &amp; battery package</div>
          <div className="rounded-2xl bg-navy p-6 text-white">
            <div className="text-sm text-[#C9D1DE]">Great news — you could be saving</div>
            <div className="font-display text-5xl font-bold tracking-tight text-lime">
              {gbp(result.annualBenefit)}
            </div>
            <div className="mt-2 text-sm text-[#C9D1DE]">a year, every year, with a system sized to your home</div>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-navyLight pt-4 text-sm text-[#C9D1DE]">
              <div>
                That&apos;s about
                <div className="font-display text-lg font-bold text-white">{gbp(result.monthlySaving)}/mo</div>
              </div>
              <div>
                {currentMonthlyBill == null ? (
                  <>
                    Your current bill
                    <div className="font-display text-lg font-bold text-lime">Not found</div>
                  </>
                ) : (
                  <>
                    Your bill, today ~{gbp(currentMonthlyBill)}/mo, could drop to
                    <div className="font-display text-lg font-bold text-lime">
                      ~{gbp(result.newMonthlyBill ?? currentMonthlyBill)}/mo
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <p className="m-0 text-xs leading-relaxed text-mutedLight">
            Illustrative estimate from the figures we read, not a verified survey. Panel count, battery size and
            system price are on your Energy Plan — checked by a certified expert before anything is ordered.
          </p>
          <div className="flex flex-col gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="min-h-[52px] rounded-xl border-2 border-border px-4"
            />
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="Where should we send it?"
              className="min-h-[52px] rounded-xl border-2 border-border px-4"
            />
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              type="tel"
              autoComplete="tel"
              placeholder="Phone number (optional)"
              className="min-h-[52px] rounded-xl border-2 border-border px-4"
            />
            <EmailPlanConsent
              checked={emailConsent}
              onCheckedChange={(next) => {
                setEmailConsent(next);
                setPreviewError(null);
              }}
            />
            {previewError && (
              <p className="m-0 text-sm font-semibold text-red-600">{previewError}</p>
            )}
            <button
              type="button"
              disabled={
                !emailConsent ||
                !isValidEmail(email) ||
                sendPreviewEmail.isPending ||
                billDataRef.current == null
              }
              onClick={() => {
                void (async () => {
                  const customerName = name.trim();
                  const customerEmail = email.trim();
                  const customerPhone = phone.trim();
                  const billData = billDataRef.current;
                  if (!billData || !emailConsent || !isValidEmail(customerEmail)) return;

                  setPreviewError(null);
                  try {
                    await sendPreviewEmail.mutateAsync({
                      emailConsent: true,
                      customerName,
                      customerEmail,
                      ...(customerPhone ? { customerPhone } : {}),
                      billData,
                      includesEv: ev,
                      includesHeatPump: heatPump,
                      estimate: {
                        annualBenefit: result.annualBenefit,
                        monthlySaving: result.monthlySaving,
                        panels: result.panels,
                        kwp: result.kwp,
                        batteryKwh: result.battery,
                        currentMonthlyBill: currentMonthlyBill,
                      },
                    });
                  } catch (err) {
                    const fallback = "We couldn't send your Energy Plan email. Please try again.";
                    let message = fallback;
                    if (err instanceof ApiError) {
                      if (err.errors?.emailConsent?.[0]) {
                        message = err.errors.emailConsent[0];
                      } else if (err.errors?.customerEmail?.[0]) {
                        message = err.errors.customerEmail[0];
                      } else if (err.message && err.status !== 0) {
                        message = err.message;
                      }
                    }
                    setPreviewError(message);
                    toast.error(message);
                    return;
                  }

                  if (billFileRef.current) setPendingBillFile(billFileRef.current);
                  setPendingBillData(billData);
                  if (customerName || customerEmail || customerPhone) {
                    setPendingPlanContact({ customerName, customerEmail, customerPhone });
                  }
                  navigate("/plan?entry=bill");
                })();
              }}
              className="min-h-[56px] rounded-2xl bg-lime px-6 font-display text-lg font-bold tracking-tight text-ink disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sendPreviewEmail.isPending ? "Sending your plan…" : "See my full Energy Plan →"}
            </button>
            <p className="m-0 text-center text-xs text-mutedLight">
              We&apos;ll also email a copy to {email || "the address above"}
            </p>
          </div>
          <button
            type="button"
            onClick={startAgain}
            className="min-h-[48px] rounded-2xl border-2 border-border px-6 font-semibold text-ink"
          >
            Wrong bill? Start again
          </button>
        </div>
      )}
    </div>
  );
}
